import React, { useEffect, useState, useCallback } from 'react';
import { dataDeletionAPI } from '../services/api';
import { Table, Badge, Select, Card, Pagination, useToast } from '../components/UI';

const STATUS_META = {
  pending:    { label: 'Pending',    color: 'var(--gold)',   bg: 'var(--gold-bg)' },
  processing: { label: 'Processing', color: 'var(--blue)',   bg: 'var(--blue-bg)' },
  completed:  { label: 'Completed',  color: 'var(--accent)', bg: 'var(--accent-light)' },
};

export default function DataDeletion() {
  const { show, ToastEl } = useToast();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [meta,    setMeta]    = useState({});
  const [statusF, setStatusF] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dataDeletionAPI.list({ page, limit: 15, status: statusF });
      setItems(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load requests', 'error'); }
    finally { setLoading(false); }
  }, [page, statusF]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id, status) {
    try {
      await dataDeletionAPI.updateStatus(id, status);
      show(status === 'completed' ? 'Marked as completed' : 'Status updated');
      load();
    } catch { show('Failed to update', 'error'); }
  }

  const columns = [
    { key: 'email', label: 'Email', render: v => (
      <span style={{ fontWeight: 500 }}>{v}</span>
    )},
    { key: 'reason', label: 'Reason', render: v => (
      <span style={{ fontSize: 12, color: 'var(--text2)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 300 }}>
        {v || <em style={{ color: 'var(--text3)' }}>No reason provided</em>}
      </span>
    )},
    { key: 'status', label: 'Status', render: v => {
      const m = STATUS_META[v] || STATUS_META.pending;
      return (
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: m.bg, color: m.color }}>
          {m.label}
        </span>
      );
    }},
    { key: 'created_at', label: 'Requested', render: v => (
      new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    )},
    { key: 'processed_at', label: 'Processed', render: (v, row) => (
      v
        ? <span style={{ fontSize: 12 }}>{new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{row.processed_by_name ? ` · ${row.processed_by_name}` : ''}</span>
        : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
    )},
    { key: 'actions', label: '', render: (_, row) => (
      row.status !== 'completed' ? (
        <Select
          value={row.status}
          onChange={e => updateStatus(row.id, e.target.value)}
          style={{ fontSize: 12, padding: '4px 8px', width: 130 }}
        >
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
        </Select>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Done</span>
      )
    )},
  ];

  const counts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});
  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            Data Deletion Requests
            {pendingCount > 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: 'var(--gold-bg)', color: 'var(--gold)' }}>
                {pendingCount} pending
              </span>
            )}
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
            Manage account and data deletion requests submitted by users.
          </p>
        </div>
      </div>

      {/* Status filter pills */}
      {Object.keys(counts).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_META).map(([key, m]) => counts[key] > 0 && (
            <button
              key={key}
              onClick={() => { setStatusF(statusF === key ? '' : key); setPage(1); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                border: `1px solid ${statusF === key ? m.color : 'var(--border)'}`,
                background: statusF === key ? m.bg : 'transparent',
                color: statusF === key ? m.color : 'var(--text2)', cursor: 'pointer',
              }}
            >
              {m.label}
              <span style={{ background: m.color, color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 600 }}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <Select value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }} style={{ width: 160 }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
        </Select>
      </div>

      <Card>
        <Table columns={columns} data={items} loading={loading} emptyMsg="No deletion requests yet"/>
        <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage}/>
      </Card>
    </div>
  );
}
